import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import { useSettings } from '@/hooks/useProducts';
import type { HeaderProps } from '../Header';
import { useHeaderNav } from './useHeaderNav';
import { useNavDropdowns, DesktopNavLink, navLinkKey } from './NavLinks';
import BrandLogo, { useStoreName } from './BrandLogo';
import HeaderActions from './HeaderActions';
import MobileMenu from './MobileMenu';
import { useHeaderCta } from './useHeaderCta';

/** Scroll depth after which the bar frosts. */
const FROST_AT_PX = 24;

const ICON_CLASS =
  'p-2 text-ink-soft hover:text-ink min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-surface-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';
const SIGN_IN_CLASS =
  'hidden sm:inline-flex items-center px-3 py-1.5 text-sm font-medium text-ink hover:text-ink border border-line rounded-full hover:bg-surface-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';
const NAV_LINK_CLASS =
  'rounded-full px-3 py-1.5 text-sm font-medium text-ink-soft hover:text-ink hover:bg-surface-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';
const CTA_CLASS =
  'inline-flex items-center justify-center rounded-full bg-cta px-4 py-2 text-sm font-semibold text-cta-fg hover:bg-cta-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

/**
 * The `floating_glass` header (Nightfall and other app-launch templates):
 * one row — logo left, nav centre, actions and a pill CTA right — that is
 * transparent while the page sits at the top and becomes a frosted bar
 * (backdrop blur, hairline border) once scrolled. Sticky, so the page still
 * flows beneath it; a hero that wants to sit under the transparent bar reads
 * `body[data-header-floating]` and pulls itself up by the bar's height.
 * Mobile: a full-screen sheet with the CTA pinned at the bottom.
 *
 * Built from the same parts as Header.tsx (useHeaderNav, DesktopNavLink,
 * HeaderActions, BrandLogo, MobileMenu), so nav sources, dropdowns, the
 * account menu and app pages behave identically on every variant.
 */
export default function FloatingGlassHeader({ variant = 'floating_glass' }: HeaderProps = {}) {
  const { t } = useTranslation(['nav', 'common']);
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useUIStore();
  const { data: settingsData } = useSettings();
  const announcementText = settingsData?.settings?.announcement_bar_text;
  const announcementEnabled = settingsData?.settings?.announcement_bar_enabled;
  const [scrolled, setScrolled] = useState(false);
  const storeName = useStoreName();
  const { navLinks, visibleAppSections, inReplaceMode, showEcommerce, showCourses, LogoIcon } = useHeaderNav();
  const dropdowns = useNavDropdowns(inReplaceMode);
  const cta = useHeaderCta();

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > FROST_AT_PX);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Let a hero variant know it may run up under the transparent bar.
  useEffect(() => {
    document.body.dataset.headerFloating = 'true';
    return () => {
      delete document.body.dataset.headerFloating;
    };
  }, []);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-40 border-b transition-[background-color,border-color,box-shadow] duration-300',
          scrolled
            ? 'bg-surface/80 backdrop-blur-md border-line shadow-sm'
            : 'bg-transparent border-transparent',
        )}
        data-header-variant={variant}
        data-scrolled={scrolled ? 'true' : 'false'}
        role="banner"
      >
        {/* Announcement strip: the same deliberately-dark band as Header.tsx. */}
        {announcementEnabled && announcementText && (
          <div
            className="surface-fixed-dark bg-sage-800 text-sage-100 text-xs text-center py-1.5 px-4 font-medium tracking-wide"
            role="status"
            aria-live="polite"
          >
            {announcementText}
          </div>
        )}

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <button
                className="lg:hidden -ml-2 p-2 text-ink-soft hover:text-ink min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
                onClick={toggleMobileMenu}
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? t('nav:closeMenu') : t('nav:openMenu')}
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <BrandLogo Icon={LogoIcon} size="compact" onClick={closeMobileMenu} />
            </div>

            {navLinks.length > 0 && (
              <nav
                className="hidden lg:flex items-center gap-1 rounded-full border border-line/60 bg-surface/60 px-1.5 py-1 backdrop-blur-sm"
                aria-label={t('nav:mainNavigation')}
              >
                {navLinks.map((link) => (
                  <DesktopNavLink key={navLinkKey(link)} link={link} dropdowns={dropdowns} linkClass={NAV_LINK_CLASS} />
                ))}
              </nav>
            )}

            <HeaderActions
              visibleAppSections={visibleAppSections}
              showEcommerce={showEcommerce}
              showCourses={showCourses}
              iconClass={ICON_CLASS}
              signInClass={SIGN_IN_CLASS}
            >
              {cta && (
                <Link to={cta.href} className={cn(CTA_CLASS, 'hidden sm:inline-flex')} data-header-cta>
                  {cta.label}
                </Link>
              )}
            </HeaderActions>
          </div>
        </div>
      </header>

      <MobileMenu
        layout="sheet"
        navLinks={navLinks}
        visibleAppSections={visibleAppSections}
        showEcommerce={showEcommerce}
        showCourses={showCourses}
        storeName={storeName}
        inReplaceMode={inReplaceMode}
        dropdowns={dropdowns}
        footer={
          cta ? (
            <Link to={cta.href} className={cn(CTA_CLASS, 'w-full')} onClick={closeMobileMenu} data-header-cta>
              {cta.label}
            </Link>
          ) : undefined
        }
      />
    </>
  );
}
